import { createContext, useCallback, useContext, useState } from "react";
import { api } from "@/api";

const PageContentContext = createContext(null);

// Default content matches backend page_defaults.py — used while CMS data loads
// and as a fallback if the API call fails. The actual source of truth is the API.
const FALLBACK = {
  about: {
    hero_eyebrow: "About KOSTRA",
    hero_title_pre: "We engineer the",
    hero_title_em: "food businesses",
    hero_title_post: "behind India's most ambitious operators.",
    hero_intro: "",
    pillars: [],
    services_eyebrow: "Our Services",
    services_title_pre: "Built for every part of your",
    services_title_em: "food journey",
    services_title_post: ".",
    services: [],
    cta_label: "Talk to KOSTRA",
  },
  "product-dev": {
    hero_eyebrow: "Product Development",
    hero_title_pre: "From kitchen idea to",
    hero_title_em: "market-ready",
    hero_title_post: "product.",
    hero_intro: "",
    new_product_title: "New Product Development",
    new_product_items: [],
    improvement_title: "Product Improvement",
    improvement_items: [],
    form_eyebrow: "Inquiry",
    form_title_pre: "Share your",
    form_title_em: "brief",
    form_title_post: ".",
    form_intro: "",
  },
  consultancy: {
    hero_eyebrow: "Food Consultancy",
    hero_title_pre: "Build, launch and run a",
    hero_title_em: "profitable",
    hero_title_post: "food business.",
    hero_intro: "",
    services_eyebrow: "Capabilities",
    services_title_pre: "Consultancy that",
    services_title_em: "delivers",
    services_title_post: ".",
    services: [],
    form_eyebrow: "Get In Touch",
    form_title_pre: "Tell us what you're",
    form_title_em: "building",
    form_title_post: ".",
    form_intro: "",
  },
  franchise: {
    hero_eyebrow: "Franchise Solutions",
    hero_title_pre: "The",
    hero_title_em: "FOCO",
    hero_title_post: "model — your kitchen, our brand, shared growth.",
    hero_intro: "",
    flow_eyebrow: "How it works",
    flow_title_pre: "Six steps from",
    flow_title_em: "space",
    flow_title_post: "to revenue.",
    steps: [],
    perks_eyebrow: "What you get",
    perks_title_pre: "A complete",
    perks_title_em: "cloud kitchen",
    perks_title_post: "business — out of the box.",
    perks: [],
    form_eyebrow: "Apply",
    form_title_pre: "Ready to launch your",
    form_title_em: "cloud kitchen",
    form_title_post: "?",
    form_intro: "",
  },
  contact: {
    hero_eyebrow: "Contact",
    hero_title_pre: "Let's get",
    hero_title_em: "building",
    hero_title_post: ".",
    hero_intro: "",
    form_eyebrow: "Inquiry Form",
    form_title: "Send us a message",
  },
};

export const PAGE_KEYS = Object.keys(FALLBACK);

export function PageContentProvider({ children }) {
  const [pages, setPages] = useState({});

  const fetchPage = useCallback(async (key) => {
    try {
      const { data } = await api.get(`/pages/${key}`);
      setPages((prev) => ({ ...prev, [key]: data }));
      return data;
    } catch {
      return null;
    }
  }, []);

  const invalidate = useCallback((key) => {
    setPages((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  return (
    <PageContentContext.Provider value={{ pages, fetchPage, invalidate }}>
      {children}
    </PageContentContext.Provider>
  );
}

export function usePageContent(key) {
  const ctx = useContext(PageContentContext);
  if (!ctx) {
    return { content: FALLBACK[key] || {}, ready: false };
  }
  const { pages, fetchPage } = ctx;
  // Trigger fetch on first read (idempotent thanks to setPages key check).
  if (pages[key] === undefined) {
    // fire-and-forget; component will re-render once the state updates
    fetchPage(key);
  }
  const content = pages[key] ? { ...(FALLBACK[key] || {}), ...pages[key] } : (FALLBACK[key] || {});
  return { content, ready: !!pages[key] };
}

export function usePageContentManager() {
  const ctx = useContext(PageContentContext);
  return ctx || { pages: {}, fetchPage: async () => null, invalidate: () => {} };
}
